import {
  type QuickAddRecordingResult,
  uploadRecordingToSong,
} from "@bandwith/recordings";
import { ChordProRenderer, type InlineFeedback } from "@bandwith/songs";
import {
  AuthorSelectView,
  Button,
  CheckboxToggle,
  ConfirmingButton,
  TransposeControl,
} from "@bandwith/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { ChevronLeft, Maximize2, Megaphone, Music } from "lucide-react";
import { useCallback, useMemo, useRef, useState } from "react";
import { ChordProEditPane } from "../components/ChordProEditPane";
import { FeedbackList } from "../components/FeedbackList";
import { RecordingActions } from "../components/RecordingActions";
import { RecordingsList } from "../components/RecordingsList";
import {
  PrevNextNav,
  useSetlistNavigation,
} from "../components/SetlistNavigation";
import { SongDetailSkeleton } from "../components/SongDetailSkeleton";
import { api, type SongDetail as SongDetailType } from "../lib/api";
import { useIsAdmin, useSession } from "../lib/auth";
import { songFeedbackQuery, songQuery } from "../lib/queries";
import { useEditActionParam } from "../lib/useEditActionParam";
import { useFeedbackCache } from "../lib/useFeedbackCache";
import { useShowChords } from "../lib/useShowChords";
import { useSignInRedirect } from "../lib/useSignInRedirect";
import { useSongEditor } from "../lib/useSongEditor";
import { useTranspose } from "../lib/useTranspose";
import { songDetailRoute } from "../router";

export function SongDetail() {
  const { id } = songDetailRoute.useParams();
  const { action: initialAction, from } = songDetailRoute.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { session } = useSession();
  const signInRedirect = useSignInRedirect();
  const isSignedIn = !!session?.user;
  const isAdmin = useIsAdmin();

  const editor = useSongEditor(id);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [pendingAnchor, setPendingAnchor] = useState<string | null>(null);
  const feedbackSectionRef = useRef<HTMLElement>(null);
  const notesSectionRef = useRef<HTMLDivElement>(null);

  const { showChords, handleToggle: handleChordsToggle } = useShowChords();
  const { transpose, handleChange: handleTransposeChange } = useTranspose(id);
  const [showComments, setShowComments] = useState(true);

  const { data: song, isLoading, error } = useQuery(songQuery(id));

  const { data: serverFeedback } = useQuery({
    ...songFeedbackQuery(id),
    enabled: !!song && !("error" in song),
  });
  const feedback = serverFeedback ?? [];

  const backNavigation = useSetlistNavigation({ songId: id }, from);

  const handleEditClick = (currentSong: SongDetailType) => {
    if (!isSignedIn) {
      signInRedirect(`/songs/${id}?action=edit`);
      return;
    }
    editor.startEditing(currentSong);
  };

  const updateCachedSong = useCallback(
    (update: (current: SongDetailType) => SongDetailType) => {
      queryClient.setQueryData(songQuery(id).queryKey, (current) =>
        current ? update(current) : current,
      );
    },
    [queryClient, id],
  );

  const feedbackCache = useFeedbackCache(songFeedbackQuery(id).queryKey);

  const handleFeedbackDeleted = (feedbackId: string) => {
    feedbackCache.handleFeedbackDeleted(feedbackId);
    updateCachedSong((current) =>
      current.amplifiedCommentId === feedbackId
        ? { ...current, amplifiedCommentId: null, amplifiedComment: null }
        : current,
    );
  };

  const handleAmplifiedChanged = (feedbackId: string | null) => {
    updateCachedSong((current) => ({
      ...current,
      amplifiedCommentId: feedbackId,
      amplifiedComment: feedbackId
        ? (feedback.find((f) => f.id === feedbackId) ??
          current.amplifiedComment)
        : null,
    }));
  };

  const handleAddCommentAtSelection = useCallback((anchorMarkup: string) => {
    setPendingAnchor(anchorMarkup);
    feedbackSectionRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  const handleAnchorConsumed = useCallback(() => {
    setPendingAnchor(null);
  }, []);

  const handleInlineCommentClick = useCallback((feedbackId: string) => {
    const feedbackElement = document.getElementById(`feedback-${feedbackId}`);
    if (feedbackElement) {
      feedbackElement.scrollIntoView({ behavior: "smooth" });
      feedbackElement.classList.add("ring-2", "ring-amber", "z-10");
      setTimeout(() => {
        feedbackElement.classList.remove("ring-2", "ring-amber", "z-10");
      }, 2000);
    } else {
      feedbackSectionRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, []);

  const handleLyricsAnchorClick = useCallback((anchorText: string) => {
    notesSectionRef.current?.scrollIntoView({ behavior: "smooth" });

    // Flash the matching mark element after scroll completes
    setTimeout(() => {
      const marks = notesSectionRef.current?.querySelectorAll("mark");
      if (!marks) return;

      for (const mark of marks) {
        if (
          mark.textContent?.toLowerCase().includes(anchorText.toLowerCase())
        ) {
          mark.classList.add("animate-pulse");
          setTimeout(() => mark.classList.remove("animate-pulse"), 1500);
          break;
        }
      }
    }, 500);
  }, []);

  async function getUploadUrl(
    fileName: string,
    fileType: string,
    fileSize: number,
  ) {
    const response = await api.upload.url.$get({
      query: { fileName, fileType, fileSize: String(fileSize) },
    });
    if (!response.ok) throw new Error("Failed to get upload URL");
    return response.json();
  }

  async function addRecording(
    songId: string,
    data: { storageKey: string; durationSeconds: number },
  ) {
    const response = await api.songs[":id"].recordings.$post({
      param: { id: songId },
      json: data,
    });
    if (!response.ok) throw new Error("Failed to add recording");
    return response.json();
  }

  const uploadMutation = useMutation({
    mutationFn: (file: File) =>
      uploadRecordingToSong(file, id, getUploadUrl, addRecording),
    onSuccess: (recording: QuickAddRecordingResult) => {
      updateCachedSong((current) => ({
        ...current,
        recordings: [...current.recordings, { ...recording, songId: id }],
      }));
      editor.appendRecording(recording.id);
      setUploadError(null);
    },
    onError: (err) => {
      setUploadError(err instanceof Error ? err.message : "Upload failed");
    },
  });

  const visibilityMutation = useMutation({
    mutationFn: async (input: { recordingId: string; unlisted: boolean }) => {
      const response = await api.songs[":id"].recordings[
        ":recordingId"
      ].visibility.$patch({
        param: { id, recordingId: input.recordingId },
        json: { unlisted: input.unlisted },
      });
      if (!response.ok) throw new Error("Failed to update visibility");
      return response.json();
    },
    onSuccess: (updated) => {
      updateCachedSong((current) => ({
        ...current,
        recordings: current.recordings.map((r) =>
          r.id === updated.id ? { ...r, unlisted: updated.unlisted } : r,
        ),
      }));
    },
  });

  const deleteSongMutation = useMutation({
    mutationFn: async () => {
      const response = await api.songs[":id"].$delete({ param: { id } });
      if (!response.ok) {
        throw new Error("Failed to delete song");
      }
      return response.json();
    },
    onSuccess: () => {
      navigate({ to: "/" });
    },
    onError: (err) => {
      console.error("Delete song error:", err);
    },
  });

  // During editing, recordings reflect the staged order (deletions and
  // reordering apply on save); otherwise they come straight from the cache.
  const visibleRecordings = useMemo(() => {
    const serverRecordings = song && !("error" in song) ? song.recordings : [];
    if (!editor.isEditing) return serverRecordings;
    return editor.recordingOrder.flatMap((rid) => {
      const recording = serverRecordings.find((r) => r.id === rid);
      return recording ? [recording] : [];
    });
  }, [song, editor.isEditing, editor.recordingOrder]);

  const recordingIds = useMemo(
    () => visibleRecordings.map((r) => r.id),
    [visibleRecordings],
  );

  // Auto-enter edit mode when ?action=edit is in URL (after OAuth redirect)
  const loadedSong = song && !("error" in song) ? song : null;
  useEditActionParam(
    initialAction,
    !!loadedSong && isSignedIn && !editor.isEditing,
    () => {
      if (loadedSong) editor.startEditing(loadedSong);
    },
  );

  if (isLoading) {
    return <SongDetailSkeleton />;
  }

  if (error || !song || "error" in song) {
    return (
      <div>
        <p className="text-red-muted">Song not found</p>
        <Link to="/" className="text-amber hover:underline">
          Back to songs
        </Link>
      </div>
    );
  }

  const recordingsWithMetadata = visibleRecordings.map((r, index) => ({
    id: r.id,
    filename: r.filename ?? `Recording ${index + 1}`,
    signedAudioUrl: r.signedAudioUrl ?? "",
    durationSeconds: r.durationSeconds ?? 0,
    createdAt: r.createdAt ?? new Date().toISOString(),
    unlisted: r.unlisted ?? false,
    songId: song.id,
    songTitle: song.title,
    authorName: song.author?.name ?? null,
  }));

  const inlineFeedback: InlineFeedback[] = feedback.map((f) => ({
    id: f.id,
    body: f.body,
    author: {
      name: f.author?.name ?? null,
      image: f.author?.image ?? null,
    },
  }));

  return (
    <>
      {/* Back navigation */}
      <nav className="mb-8 flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <Link
            to={backNavigation.href}
            className="group inline-flex items-center gap-2 text-sm text-text-secondary transition-colors hover:text-amber"
          >
            <span className="transition-transform duration-150 group-hover:-translate-x-1">
              <ChevronLeft className="h-4 w-4" />
            </span>
            {backNavigation.label}
          </Link>
          {editor.isEditing ? (
            <div className="flex gap-2">
              <Button
                variant="secondary"
                onClick={editor.cancel}
                disabled={editor.isSaving}
              >
                Cancel
              </Button>
              <Button onClick={editor.save} disabled={editor.isSaving}>
                {editor.isSaving ? "Saving..." : "Save"}
              </Button>
            </div>
          ) : (
            <Button variant="ghost" onClick={() => handleEditClick(song)}>
              Edit
            </Button>
          )}
        </div>
        {/* Prev/Next navigation when viewing from a setlist */}
        <PrevNextNav prev={backNavigation.prev} next={backNavigation.next} />
      </nav>

      {/* Header */}
      <header className="mb-10">
        {editor.isEditing ? (
          <>
            <input
              name="title"
              type="text"
              value={editor.title}
              onChange={(e) => editor.setTitle(e.target.value)}
              disabled={editor.isSaving}
              placeholder="Untitled Song"
              className="w-full rounded-lg border border-amber bg-bg-secondary px-4 py-2 font-display text-3xl text-text-primary placeholder-text-muted transition-colors focus:outline-none disabled:opacity-50"
            />
            <div className="mt-2 flex items-center gap-2">
              <span className="text-text-muted">by</span>
              <div className="flex-1">
                <AuthorSelectView
                  musicians={editor.musicians}
                  value={editor.authorId}
                  onChange={editor.setAuthorId}
                  onCreateMusician={editor.createMusician}
                  disabled={editor.isSaving}
                />
              </div>
            </div>
          </>
        ) : (
          <>
            <h1 className="font-display text-3xl text-text-primary">
              {song.title}
            </h1>
            {song.author?.name && (
              <p className="mt-2 text-text-muted">by {song.author.name}</p>
            )}
          </>
        )}
      </header>

      {/* Amplified Comment - prominent callout when present */}
      {song.amplifiedComment && (
        <section className="mb-10 flex gap-3 rounded-lg border border-amber/30 bg-amber/5 p-4">
          <Megaphone
            className="mt-0.5 h-4 w-4 shrink-0 text-amber"
            aria-label="Amplified comment"
          />
          <p className="text-text-primary whitespace-pre-wrap">
            {song.amplifiedComment.body}
          </p>
        </section>
      )}

      {/* Recordings section */}
      <section>
        <h2 className="mb-4 font-mono text-xs font-medium uppercase tracking-widest text-text-muted">
          Recordings ({recordingsWithMetadata.length})
        </h2>
        {recordingsWithMetadata.length > 0 ? (
          <RecordingsList
            recordings={recordingsWithMetadata}
            recordingIds={recordingIds}
            songId={song.id}
            songTitle={song.title}
            authorName={song.author?.name ?? null}
            isEditing={editor.isEditing}
            onReorder={editor.reorderRecordings}
            onDelete={editor.removeRecording}
            onToggleVisibility={(recordingId, unlisted) =>
              visibilityMutation.mutate({ recordingId, unlisted })
            }
          />
        ) : (
          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border-subtle bg-bg-secondary/50 py-12 text-center">
            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-bg-tertiary">
              <Music className="h-6 w-6 text-text-muted" aria-hidden="true" />
            </div>
            <p className="font-display text-lg text-text-secondary">
              No recordings yet
            </p>
            <p className="mt-1 text-sm text-text-muted">
              Record a demo to get started
            </p>
          </div>
        )}
        {/* Recording actions - visible in both view and edit mode, auth-gated */}
        <RecordingActions
          onRecordingComplete={(file) => uploadMutation.mutate(file)}
          autoStart={initialAction === "record"}
        />
        {uploadMutation.isPending && (
          <p className="mt-2 text-sm text-text-muted">Uploading...</p>
        )}
        {uploadError && (
          <p className="mt-2 text-sm text-red-500">{uploadError}</p>
        )}
      </section>

      {/* Notes section */}
      <section ref={notesSectionRef} className="mt-10">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-mono text-xs font-medium uppercase tracking-widest text-text-muted">
            {editor.isEditing ? (
              "Notes"
            ) : (
              <Link
                to="/songs/$id/notes"
                params={{ id }}
                search={from ? { from } : {}}
                className="inline-flex items-center gap-1.5 transition-colors hover:text-amber"
                title="Open distraction-free perform view"
              >
                Notes
                <Maximize2 className="h-3 w-3" aria-hidden="true" />
              </Link>
            )}
          </h2>
          <div className="flex items-center gap-3">
            {!editor.isEditing && showChords && (
              <TransposeControl
                value={transpose}
                onChange={handleTransposeChange}
              />
            )}
            <CheckboxToggle
              checked={showChords}
              onChange={handleChordsToggle}
              label="Chords"
            />
            {!editor.isEditing && (
              <CheckboxToggle
                checked={showComments}
                onChange={setShowComments}
                label="Comments"
              />
            )}
          </div>
        </div>
        {editor.isEditing ? (
          <ChordProEditPane
            value={editor.notes}
            onChange={editor.setNotes}
            onSubmit={editor.save}
            disabled={editor.isSaving}
            previewContent={editor.debouncedNotes}
            showChords={showChords}
          />
        ) : (
          <div className="rounded-lg border border-border-subtle bg-bg-secondary p-5">
            <ChordProRenderer
              content={song.notes || ""}
              showChords={showChords}
              transpose={transpose}
              inlineFeedback={inlineFeedback}
              showInlineComments={showComments}
              onAddCommentAtSelection={handleAddCommentAtSelection}
              onInlineCommentClick={handleInlineCommentClick}
            />
          </div>
        )}
      </section>

      {/* Feedback section */}
      <section ref={feedbackSectionRef} className="mt-10">
        <h2 className="mb-4 font-mono text-xs font-medium uppercase tracking-widest text-text-muted">
          Feedback ({feedback.length})
        </h2>
        <FeedbackList
          feedback={feedback}
          target={{ songId: song.id }}
          songAuthorId={song.authorId}
          recordings={recordingsWithMetadata}
          isAdmin={isAdmin}
          amplifiedCommentId={song.amplifiedCommentId ?? null}
          onAmplifiedChanged={handleAmplifiedChanged}
          onFeedbackCreated={feedbackCache.handleFeedbackCreated}
          onFeedbackUpdated={feedbackCache.handleFeedbackUpdated}
          onFeedbackDeleted={handleFeedbackDeleted}
          pendingAnchor={pendingAnchor}
          onAnchorConsumed={handleAnchorConsumed}
          onLyricsAnchorClick={handleLyricsAnchorClick}
        />
      </section>

      {/* Danger Zone - only visible in edit mode */}
      {editor.isEditing && (
        <section className="mt-10 border-t border-border-subtle pt-6">
          <h2 className="mb-4 font-mono text-xs font-medium uppercase tracking-widest text-text-muted">
            Danger Zone
          </h2>
          <ConfirmingButton
            onConfirm={() => deleteSongMutation.mutateAsync()}
            confirmContent="Delete?"
            disabled={deleteSongMutation.isPending || editor.isSaving}
          >
            Delete Song
          </ConfirmingButton>
        </section>
      )}
    </>
  );
}
